/* eslint-disable react/jsx-props-no-spreading */
import React, {useEffect, useRef, useState} from 'react';
import {useRouter} from 'next/router';
import _ from 'lodash';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import cx from 'classnames';

import {
  getAirtableData, getCoachingCredits, getCoachBySlug,
} from '../../api/api';
import DocumentHeader from '../../components/DocumentHeader';
import Markdown from '../../components/Markdown';
import FadeOut from '../../components/FadeOut';
import StarRating from '../../components/StarRating';
import ResumeTab from '../../components/ResumeTab';
import BookingTab from '../../components/BookingTab';
import CoachingServiceMenu from './CoachingServiceMenu';
import {useAnalytics, useAppContext} from '../../hooks';
import Button, {ButtonSizes, ButtonTypes} from '../../components/ButtonNew';
import FAQSection from '../../components/FAQSection';
import {CoachingFAQs, ServiceNames} from '../../constants/coaching';
import {airtableRoleToRoleMap} from '../../../../lib/constants/airtableTags';
import CoachAvailabilityModal from './CoachAvailabilityModal';
import Stars from '../../components/Stars';
import {Product} from '../../components/LinkedData';
import Link from '../../components/Link';
import {createURLWithParams} from '../../util';
import {CoachAvailableStatuses, CoachAvailableStatusesAdmin} from '../../../../lib/constants/coaching';

const isAvailableCoach = (coach, currentUser) => {
  if (!coach) return false;

  if (currentUser?.role === 'admin'
    ? !CoachAvailableStatusesAdmin.includes(coach.Status)
    : !CoachAvailableStatuses.includes(coach.Status)) return false;

  return true;
};

const Placeholder = () => (
  <div className="animate-pulse max-w-6xl w-full bg-white h-screen p-8 md:p-16">
    <div className="flex flex-col items-center md:items-start">
      <div className="h-24 w-24 md:h-32 md:w-32 rounded-full bg-gray-100" />
      <div className="flex flex-col items-center md:items-start w-full mt-8 space-y-2">
        <div className="h-6 w-1/2 bg-gray-100 rounded" />
        <div className="h-4 w-1/3 bg-gray-100 rounded" />
      </div>
    </div>
  </div>
);

const CoachProfilePage = (props) => {
  const router = useRouter();
  const {tracker} = useAnalytics();
  const {currentUser, loading} = useAppContext();
  const [coachContent, setCoachContent] = useState();
  const [readMore, setReadMore] = useState(false);
  const [ratings, setRatings] = useState(3);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [credits, setCredits] = useState();
  const [coach, setCoach] = useState(null);
  const servicesRef = useRef();

  const {coach: slug} = props;

  useEffect(() => {
    const getAndSetCoach = async () => {
      const c = await getCoachBySlug(slug);

      // TODO: Don't redirect for CoachAwayStatuses. Instead, disable booking
      if (!isAvailableCoach(c, currentUser)) {
        router.replace('/coaching');
        return;
      }

      const data = await getAirtableData({recordID: c['Content Links']}, 'coachContent');

      setCoach(c);
      setCoachContent(data.map((d) => d.fields));

      tracker.coachProfileView({coach: c.slug});
    };

    if (!loading) getAndSetCoach();
  }, [slug, tracker, loading]);

  useEffect(() => {
    if (!currentUser) return;
    getCoachingCredits().then((resp) => {
      setCredits(resp.credits);
    });
  }, [currentUser]);

  if (!coach) return <Placeholder />;

  const pageTitle = coach.Name && coach.Title
    ? `Get Coached by ${coach.Title} ${coach.Name}`
    : 'Book a Coaching Session';

  const description = coach.Name && coach.Bio
    ? `Book a mock interview with ${coach.Name}. ${coach.Bio}`
    : 'Book a coaching session for expert mock interviews, resume help, and more.';

  const imageURL = _.get(coach, 'Photo[0].url');
  const thumbnailURL = coach.Thumbnail || imageURL;

  const hasCoaching = _.some(coach?.Services, (service) => ['Mock Interviews', 'Career Coaching', 'Negotiation', 'Communication', 'Communication Skills'].includes(service));
  const hasResumeReview = _.some(coach?.Services, (service) => ['Resume Review', 'Resume Review Call'].includes(service));

  let featuredSection;

  if (!coachContent?.length) {
    featuredSection = null;
  } else {
    featuredSection = (
      <div className="CoachingProfilePage__featured-section">
        <div className="CoachingProfilePage__header">Featured in</div>
        {coachContent.map((c) => (
          <a key={c.Title} className="CoachingProfilePage__featured-link" href={c.Link} target="_blank" rel="noopener noreferrer">
            {c.Title}
          </a>
        ))}
      </div>
    );
  }

  const toggleRatings = () => setRatings(ratings + 10);

  const onClickServices = () => {
    servicesRef.current.scrollIntoView({behavior: 'smooth'});
  };

  const testimonialsSection = coach && !_.isEmpty(coach.testimonials) && (
    <div
      className="CoachingProfilePage__testimonials-section max-w-2xl"
      id="reviews"
    >
      <div className="text-lg md:text-xl font-bold text-gray-900">
        {`Reviews of ${coach['First name']}'s coaching`}
      </div>
      <div className="flex flex-col divide-y divide-gray-200">
        {coach.testimonials.slice(0, ratings).map((t) => (
          <div key={t.comment} className="flex flex-col items-start py-4 gap-2">
            <Stars />
            {t.comment}
          </div>
        ))}
      </div>
      {coach.testimonials.length > (ratings + 1) && (
        <Button
          className="w-full my-4 md:my-6"
          buttonType={ButtonTypes.TERTIARY}
          onClick={() => toggleRatings()}
        >
          See more reviews
        </Button>
      )}
    </div>
  );

  const roleItems = coach.Role?.map((role) => ({
    label: role,
    url: createURLWithParams('/coaching', {role: airtableRoleToRoleMap[role]}),
  }));

  const skillItems = coach.Skills?.map((skill) => ({
    label: skill.name,
    url: createURLWithParams(`/coaching/${skill.id}`),
  }));

  const industryItems = coach.Industries?.map((industry) => ({
    label: industry.name,
    url: createURLWithParams('/coaching', {industry: industry.id}),
  }));

  const companyItems = coach.Companies?.map((company) => ({
    label: company,
    url: createURLWithParams('/coaching', {company: company.toLowerCase()}),
  }));

  const TagList = ({items, limit = 4, className}) => {
    const visibleItems = items.slice(0, limit);
    const hiddenItems = items.slice(limit);

    return (
      <div className={cx('flex flex-wrap flex-1 gap-2', className)}>
        {visibleItems.map((item) => (
          <Link
            key={item.label}
            className="rounded-md bg-gray-100 px-2 py-1 font-medium whitespace-nowrap"
            href={item.url}
          >
            {item.label}
          </Link>
        ))}

        {hiddenItems.length > 0 && (
          <OverlayTrigger
            placement="top"
            overlay={(
              <Tooltip>
                {hiddenItems.map((item) => (
                  <div key={item.label}>{item.label}</div>
                ))}
              </Tooltip>
            )}
          >
            <span className="text-indigo-600 cursor-pointer rounded-md px-2 py-1 font-medium whitespace-nowrap">
              {`+${hiddenItems.length} more`}
            </span>
          </OverlayTrigger>
        )}
      </div>
    );
  };

  const MobileTagList = ({items, className}) => (
    <FadeOut left right className={cx('flex-1', className)}>
      <div className="flex overflow-x-auto scrollbar-hidden">
        {items.map((item) => (
          <Link
            key={item}
            className="rounded-md bg-gray-100 px-2 py-1 font-medium ml-2 whitespace-nowrap"
            href={item.url}
          >
            {item.label}
          </Link>
        ))}
      </div>
    </FadeOut>
  );

  const numRatings = coach['Total number of sessions completed'];
  const rating = Math.round(Number(coach.Rating)) || 0;
  const displayRating = Math.round(Number(coach.Rating) * 10) / 10 || 0;
  const shouldShowRating = numRatings > 2 && rating !== 0;

  const FAQs = CoachingFAQs.filter(
    (FAQ) => FAQ.category.some((i) => coach?.Services?.includes(ServiceNames[i])),
  );

  return (
    <div className="flex justify-center bg-white">
      <div className="flex md:items-start p-8 md:p-16 w-full max-w-6xl">
        <DocumentHeader
          title={pageTitle}
          metaDescription={description}
          socialDescription={description}
          imgPath={thumbnailURL}
          noindex={coach.Noindex}
        />
        <CoachAvailabilityModal
          coach={coach}
          isOpen={availabilityOpen}
          calendlyURL={coach['Calendly link']}
          onClose={() => setAvailabilityOpen(false)}
        />
        {shouldShowRating && (
          <Product
            name={coach.Name}
            image={coach.Photo[0].url}
            aggregateRating={{
              ratingValue: coach.Rating,
              bestRating: 5,
              ratingCount: numRatings,
            }}
          />
        )}
        <div className="max-w-2xl w-full">
          <div>
            <div className="overflow-hidden">
              <div className="flex flex-col items-center md:items-start">
                <img
                  className="mb-4 h-24 w-24 md:h-32 md:w-32 rounded-full object-cover"
                  alt={coach.Name}
                  src={coach.Photo[0].url}
                />
              </div>
              <h1 className="text-2xl md:text-3xl text-center md:text-left font-bold text-gray-900">{coach.Name}</h1>
              <h2 className="ml-0.5 font-semibold text-lg text-center md:text-left text-gray-500">{coach.Title}</h2>
              {shouldShowRating && (
                <div className="mb-4 flex items-center justify-center md:justify-start">
                  <StarRating initialRating={rating} displayOnly allowHalf />
                  <a href="#reviews" className="ml-2 font-medium text-gray-500">
                    {`${displayRating} (${numRatings} reviews)`}
                  </a>
                </div>
              )}

              {/* Coach bio for mobile screens is truncated */}
              <div className="my-4 md:hidden text-gray-600 text-base prose">
                <Markdown>
                  {readMore
                    ? coach.Bio
                    : _.truncate(coach.Bio, {length: 120})}
                </Markdown>
                {!readMore && coach.Bio.length > 120 && (
                <button
                  type="button"
                  className="text-indigo"
                  onClick={() => setReadMore(true)}
                >
                  Read more
                </button>
                )}
              </div>

              {/* Coach bio for desktop */}
              <div className="hidden md:block my-6 text-gray-600 text-base prose">
                <Markdown>
                  {readMore
                    ? coach.Bio
                    : _.truncate(coach.Bio, {length: 300, separator: ' '})}
                </Markdown>
                {!readMore && coach.Bio.length > 300 && (
                <button
                  type="button"
                  className="text-indigo"
                  onClick={() => setReadMore(true)}
                >
                  Read more
                </button>
                )}
              </div>

              {/* Coach structured tags */}
              <div className="border rounded-lg mb-6">
                <ul>
                  {coach?.Role && (
                    <li className="flex p-3 items-center border-b last:border-b-0">
                      <div className="w-24 font-medium">Roles</div>
                      <TagList items={roleItems} className="hidden lg:flex" />
                      <MobileTagList items={roleItems} className="block lg:hidden" />
                    </li>
                  )}
                  {coach?.Skills && (
                    <li className="flex p-3 items-center border-b last:border-b-0">
                      <div className="w-24 font-medium">Skills</div>
                      <TagList items={skillItems} className="hidden lg:flex" />
                      <MobileTagList items={skillItems} className="block lg:hidden" />
                    </li>
                  )}
                  {coach?.Industries && (
                    <li className="flex p-3 items-center border-b last:border-b-0">
                      <div className="w-24 font-medium">Industries</div>
                      <TagList items={industryItems} className="hidden lg:flex" />
                      <MobileTagList items={industryItems} className="block lg:hidden" />
                    </li>
                  )}
                  {coach?.Companies && (
                  <li className="flex p-3 items-center border-b last:border-b-0">
                    <div className="w-24 font-medium">Companies</div>
                    <TagList items={companyItems} className="hidden lg:flex" />
                    <MobileTagList items={companyItems} className="block lg:hidden" />
                  </li>
                  )}
                </ul>
              </div>
            </div>

            {/* Display full booking tab in mobile or lightweight version in desktop */}
            <div className="lg:hidden mb-2">
              {hasCoaching && (
                <div className="mb-4">
                  <BookingTab
                    coach={coach}
                    credits={credits}
                    trackerEvent={() => tracker.coachProfileClickBookWithCredits(
                      {coach: coach.slug},
                    )}
                    onClickServices={onClickServices}
                    onClickPreviewAvailability={() => setAvailabilityOpen(true)}
                  />
                </div>
              )}
              {hasResumeReview && (
                <div className="mb-4">
                  <ResumeTab
                    coach={coach}
                    trackerEvent={() => tracker.coachProfileClickResumeUpload({coach: coach.slug})}
                  />
                </div>
              )}
            </div>

            <div className="hidden lg:block mb-8">
              {hasCoaching && credits > 0 && (
                <div className="mb-4 flex flex-row justify-between gap-4 px-5 py-4 rounded-lg bg-white border border-gray-200">
                  <div>
                    <h3 className="mb-1.5 text-xl font-semibold text-black">{`Book a session with ${coach['First name']}`}</h3>
                    <p className="text-base font-regular text-gray-600">{`You have ${credits} session${credits > 1 ? 's' : ''} remaining.`}</p>
                  </div>
                  <div className="flex flex-col">
                    <Button
                      size={ButtonSizes.LARGE}
                      className="mb-2 whitespace-nowrap"
                      href={`/coaching/booking?coach=${coach.slug}`}
                      onClick={() => tracker.coachProfileClickBookWithCredits({
                        coach: coach.slug,
                      })}
                    >
                      Book now
                    </Button>
                    <Button
                      buttonType={ButtonTypes.TERTIARY}
                      onClick={() => setAvailabilityOpen(true)}
                    >
                      Preview availability
                    </Button>
                  </div>
                </div>
              )}

              {hasResumeReview && (
                <div className="mb-4 flex flex-row justify-between gap-4 px-5 py-4 rounded-lg bg-white border border-gray-200">
                  <div>
                    <h3 className="mb-1.5 text-xl font-semibold text-black">Get your resume reviewed</h3>
                    <p className="text-base font-regular text-gray-600">{`${coach['First name']} is on Exponent's expert resume review team`}</p>
                  </div>
                  <div className="flex flex-col">
                    <Button
                      size={ButtonSizes.LARGE}
                      className="mb-2 whitespace-nowrap"
                      href={coach.Services.includes('Resume Review') ? `/resume/upload?coach=${coach.slug}&name=${coach.Name}` : `/resume/upload?from=call&coach=${coach.slug}&name=${coach.Name}`}
                      onClick={() => tracker.coachProfileClickResumeUpload({
                        coach: coach.slug,
                      })}
                    >
                      Upload now
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Display coach service menu or direct booking link */}
            <div ref={servicesRef}>
              {hasCoaching && (
                <CoachingServiceMenu coach={coach} hasCredits={credits > 0} />
              )}
            </div>
          </div>
          {featuredSection}

          {testimonialsSection}
          {hasCoaching && (
            <div className="mt-8 w-full flex flex-col max-w-3xl">
              <h2 className="text-lg md:text-xl font-bold text-black">Frequently asked questions</h2>
              <FAQSection items={FAQs} />
            </div>
          )}
        </div>
        <div className="hidden lg:flex flex-col ml-12 w-full max-w-sm sticky top-8">
          {hasCoaching && (
            <BookingTab
              coach={coach}
              credits={credits}
              trackerEvent={() => tracker.coachProfileClickBookWithCredits({coach: coach.slug})}
              onClickServices={onClickServices}
              onClickPreviewAvailability={() => setAvailabilityOpen(true)}
            />
          )}
          {hasResumeReview && !hasCoaching && (
            <ResumeTab
              coach={coach}
              trackerEvent={() => tracker.coachProfileClickResumeUpload({coach: coach.slug})}
            />
          )}
          {coach && !_.isEmpty(coach.testimonials) && (
            <div className="ml-4 mt-8">
              <span className="text-lg font-semibold text-gray-800">
                {`Reviews of ${coach['First name']}'s coaching`}
              </span>
              <div className="mt-2 flex flex-col space-y-4">
                {coach.testimonials.slice(0, 2).map((t) => (
                  <div key={t.comment} className="flex flex-col items-start">
                    <Stars />
                    <span className="mt-3 text-gray-600 text-sm">{t.comment}</span>
                  </div>
                ))}
              </div>
              <Button
                buttonType={ButtonTypes.TERTIARY}
                className="ml-0 pl-0"
                href="#reviews"
              >
                See all reviews
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoachProfilePage;
